import { Modal, Input, Radio, RadioChangeEvent, message, Popover } from 'antd';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { collectOrCancel, submitQuestionFeedback, getQuestionExtraInfo } from '@/apis';
import { IQuestionActions } from '@/types';
import './index.less';
import DrawerCaculator from './caculator-drawer';
import Draft from './question-draft';
import {
    QuestionCalculatorIcon,
    QuestionCareIcon,
    QuestionCollectIcon,
    QuestionDraftIcon,
    QuestionFeedbackIcon,
    QuestionFontSizeIcon,
} from '@/svgs';
import { observer } from 'mobx-react';
import { useAppStore } from '@/store';
import { useAppContext } from '@/context/app-contex';
import { useTranslation } from 'react-i18next';

const QuestionActions: React.FC<IQuestionActions> = props => {
    const { businessParam, questionBusinessParamMap } = useAppContext();
    const { themeStore, commonStore } = useAppStore();
    const { theme } = themeStore;
    const { t, i18n } = useTranslation();

    const { questionItem, isAnalysis = false, parentId, removeCollect, removeLucaAnswer } = props;
    const { projectId, subjectId, questionId, parentQuestionId } = questionItem;
    const [collectStatus, setCollectStatus] = useState(false);
    const [feedbackVisible, setFeedbackVisible] = useState(false);
    const [feedbackType, setFeedbackType] = useState<number>(0);
    const [calcVisible, setCalcVisible] = useState(false);
    const [draftVisible, setDraftVisible] = useState(false);
    const inputValue = useRef<string>('');

    const { answerFunction, singleQuestionCallback } = useAppContext();

    const fontSizeList = [
        { label: t('actions.fontSmall'), value: '14px', lineHeight: '26px' },
        { label: t('actions.fontMedium'), value: '16px', lineHeight: '30px' },
        { label: t('actions.fontLarge'), value: '18px', lineHeight: '34px' },
    ];

    const errorOptions = [
        { label: t('actions.typo'), value: 1 },
        { label: t('actions.answerWrong'), value: 2 },
        { label: t('actions.layoutWrong'), value: 3 },
        { label: t('actions.imageBlur'), value: 4 },
        { label: t('actions.answerAmbiguous'), value: 5 },
        { label: t('actions.correctionWrong'), value: 7 },
        { label: t('actions.otherWrong'), value: 6 },
    ];

    const fetchQuestionExtraInfo = async () => {
        if (!questionItem.questionId) {
            return;
        }
        const ret = await getQuestionExtraInfo({
            questionId: questionId,
            dataTypeList: [1, 2],
            businessParam: businessParam
                ? {
                      ...businessParam,
                      ...(questionBusinessParamMap?.[questionId] || questionBusinessParamMap?.[parentQuestionId] || {}),
                  }
                : undefined,
        });
        if (ret.status === 0) {
            setCollectStatus(ret.result.isFavorite);
            commonStore.setConfig({
                ...commonStore.common,
                pastPaperList: ret.result.pastPaperList,
            });
        }
    };

    useEffect(() => {
        fetchQuestionExtraInfo();
        setDraftVisible(false); // 切题是关闭 草稿纸
        if (questionItem.questionType !== 5) {
            singleQuestionCallback?.(questionItem);
        }
        return () => {
            singleQuestionCallback?.();
        };
    }, [questionId]);

    const handleCollect = async () => {
        setCollectStatus(!collectStatus);
        const ret = await collectOrCancel({
            handleType: !collectStatus ? 1 : 0,
            paperId: undefined,
            projectId: projectId,
            questionId: questionId,
            questionIds: collectStatus ? (parentId ? [questionId, parentId] : [questionId]) : undefined,
            subjectId: subjectId,
            businessParam: businessParam
                ? {
                      ...businessParam,
                      ...(questionBusinessParamMap?.[questionId] || {}),
                  }
                : undefined,
        });
        if (ret.status === 0) {
            message.success(!collectStatus ? t('actions.collected') : t('actions.uncollected'));
        } else {
            setCollectStatus(collectStatus);
        }
    };

    const handleOpenDraft = () => {
        setDraftVisible(true);
    };

    const handleOpenError = () => {
        setFeedbackVisible(true);
    };

    const handleOpenCalculator = () => {
        setCalcVisible(true);
    };

    const goAnswer = async () => {
        answerFunction?.(questionItem);
    };

    const handleCheckOptions = (e: RadioChangeEvent) => {
        setFeedbackType(e.target.value);
    };

    const handleSaveFeedback = async () => {
        // 保存纠错
        if (!feedbackType) {
            message.warning(t('actions.selectErrorType'));
            return;
        }
        if (!inputValue.current) {
            message.warning(t('actions.inputErrorContent'));
            return;
        }

        const ret = await submitQuestionFeedback({
            projectId,
            subjectId,
            type: feedbackType,
            content: inputValue.current,
            questionId,
        });
        if (ret.status === 0) {
            inputValue.current = '';
            setFeedbackType(0);
            message.success(t('actions.submitted'));
            setFeedbackVisible(false);
        }
    };

    const handleChangeFeedbackValue = (e: any) => {
        inputValue.current = e.target.value;
    };

    const handleClickFontSize = (value: string, lineHeight: string) => {
        localStorage.setItem('subTikuSize', value);
        document.documentElement.style.setProperty('--root-size', value);
        localStorage.setItem('subLineHeight', lineHeight);
        document.documentElement.style.setProperty('--root-line-height', lineHeight);
        themeStore.setTheme({ ...theme, rootSize: value });
    };

    return (
        <>
            <ul className={classNames('sub-tiku__question-tools')}>
                {props.children}
                <li className="sub-tiku__question-tool-item sub-tiku__question-draft" onClick={handleOpenDraft}>
                    <QuestionDraftIcon title={t('actions.draft')} />
                </li>
                {!removeCollect && i18n.language !== 'en' && (
                    <li
                        className={classNames('sub-tiku__question-tool-item', {
                            'sub-tiku__had-like-question': collectStatus,
                            'sub-tiku__not-like-question': !collectStatus,
                        })}
                        onClick={handleCollect}
                    >
                        <QuestionCollectIcon title={t('actions.collect')} />
                    </li>
                )}

                {isAnalysis && !removeLucaAnswer && (
                    <li className="sub-tiku__question-tool-item sub-tiku__care-question" title="答疑" onClick={goAnswer}>
                        <QuestionCareIcon title={t('actions.answer')} />
                    </li>
                )}

                <li className="sub-tiku__question-tool-item sub-tiku__feedback-question" onClick={handleOpenError}>
                    <QuestionFeedbackIcon title={t('actions.feedback')} />
                </li>

                <li className="sub-tiku__question-tool-item sub-tiku__calculator-question" onClick={handleOpenCalculator}>
                    <QuestionCalculatorIcon title={t('actions.calculator')} />
                </li>

                <Popover
                    content={
                        <div className="sub-tiku__font-set">
                            {fontSizeList.map(item => (
                                <div
                                    className={classNames({
                                        'sub-tiku__font-active': theme.rootSize === item.value,
                                    })}
                                    key={item.value}
                                    onClick={() => handleClickFontSize(item.value, item.lineHeight)}
                                >
                                    {item.label}
                                </div>
                            ))}
                        </div>
                    }
                    placement="bottomRight"
                >
                    <li className="sub-tiku__question-tool-item sub-tiku__font-question">
                        <QuestionFontSizeIcon />
                    </li>
                </Popover>
            </ul>
            <Modal
                afterClose={() => setFeedbackType(0)}
                className="sub-tiku__question-error-module"
                destroyOnClose
                footer={
                    <div>
                        <div className="sub-tiku__tiku-default-btn" onClick={() => setFeedbackVisible(false)} style={{ marginRight: 16 }}>
                            {t('actions.cancel')}
                        </div>
                        <div className="sub-tiku__tiku-default-btn sub-tiku__tiku-plain-btn" onClick={() => handleSaveFeedback()}>
                            {t('actions.confirm')}
                        </div>
                    </div>
                }
                width={600}
                onCancel={() => setFeedbackVisible(false)}
                title={t('actions.errorTitle')}
                open={feedbackVisible}
            >
                <div className="sub-tiku__question-error-tip">
                    <div className="sub-tiku__error-radio" />
                    {t('actions.errorTip')}
                </div>
                <Radio.Group onChange={handleCheckOptions} value={feedbackType}>
                    {errorOptions.map(item => (
                        <Radio key={item.value} value={item.value}>
                            {item.label}
                        </Radio>
                    ))}
                </Radio.Group>
                <p className="sub-tiku__questionErrorTip" style={{ marginTop: 20, marginBottom: 12 }}>
                    {t('actions.errorNotice')}
                </p>
                <Input.TextArea bordered={false} maxLength={100} onChange={handleChangeFeedbackValue} placeholder={t('actions.errorInputTip')} showCount />
            </Modal>

            <DrawerCaculator closed={() => setCalcVisible(false)} visible={calcVisible} />

            {draftVisible && <Draft close={() => setDraftVisible(false)} questionId={questionId} />}
        </>
    );
};

export default observer(QuestionActions);
